import { useCallback } from 'react';
import { ListTasksResponseV2 } from '@weave/schema-gen-ts/dist/schemas/task-center/v2/task_service.pb';
import { Call } from '@weave/schema-gen-ts/dist/shared/call-intelligence/v2/call_intelligence.pb';
import { InfiniteData, QueryKey, useQueryClient } from 'react-query';
import { type CallRecord, type Task, TaskType } from '@frontend/api-call-intel';
import { CallInsightApi } from './use-call-insight-controller';

type UseTaskHandlersProps = {
  queryKey: QueryKey; // The query key for the tasks list
  emitEvent: CallInsightApi['emitEvent'];
  updateCallInsight: (args: { updatedCallIntelInsights: Call }) => void;
};

export const useTaskHandlers = ({ emitEvent, queryKey, updateCallInsight }: UseTaskHandlersProps) => {
  const queryClient = useQueryClient();

  // Handler for updating all tasks
  const handleTasksUpdate = useCallback(
    (call: CallRecord, updatedTasks: Task[]) => {
      const prev = queryClient.getQueryData<InfiniteData<ListTasksResponseV2>>(queryKey);
      if (!prev) return;
      queryClient.setQueryData<InfiniteData<ListTasksResponseV2>>(queryKey, {
        ...prev,
        pages: prev.pages.map((page) => ({
          ...page,
          tasks: (page.tasks ?? []).map((task) => {
            const updatedTask = updatedTasks.find(({ id }) => id === task.id);

            return updatedTask ?? task;
          }),
        })),
      });
      emitEvent('tasksUpdated', { call: call as Call, tasks: updatedTasks });
    },
    [emitEvent, queryClient, queryKey]
  );

  // Handler for deleting a single task
  const handleTaskDelete = useCallback(
    (call: CallRecord, taskId: string) => {
      const prev = queryClient.getQueryData<InfiniteData<ListTasksResponseV2>>(queryKey);

      if (!prev) return;

      const updatedPages = prev.pages.map((page) => ({
        ...page,
        tasks: (page.tasks ?? []).filter((task) => task.id !== taskId),
      }));

      const remainingTasks = updatedPages.flatMap((page) => page.tasks ?? []);

      queryClient.setQueryData<InfiniteData<ListTasksResponseV2>>(queryKey, {
        ...prev,
        pages: updatedPages,
      });

      const remainingTypes = Array.from(new Set(remainingTasks.map((task) => task.type))) as TaskType[];

      const updatedCall = {
        ...call,
        taskCount: call.taskCount ? Math.max(call.taskCount - 1, 0) : 0,
        taskTypes: remainingTypes,
      } as Call;

      updateCallInsight({
        updatedCallIntelInsights: updatedCall,
      });

      emitEvent('taskDeleted', { call: updatedCall, taskId });
    },
    [emitEvent, queryClient, queryKey, updateCallInsight]
  );

  return {
    handleTasksUpdate,
    handleTaskDelete,
  };
};
